"use client";

import React from "react";
import { Box, IconButton, Button, Typography } from "@mui/material";
import { usePagination, DOTS } from "@/src/hooks/usePagination";
import {
  caretLeftSx,
  caretRightEndSx,
} from "@/src/components/common/broadsheet/tokens";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  color?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  color,
}: PaginationProps) {
  const paginationRange = usePagination({
    currentPage,
    totalPages,
    siblingCount,
  });

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        pt: 2,
      }}
    >
      <IconButton
        onClick={handlePrevious}
        disabled={currentPage === 1}
        aria-label="Go to previous page"
        sx={{
          border: 1,
          borderColor: "custom.ruleHairline",
          width: 36,
          height: 36,
          "&:hover": {
            backgroundColor: "action.hover",
            borderColor: "primary.main",
          },
          "&.Mui-disabled": {
            borderColor: "custom.ruleHairline",
            color: "action.disabled",
          },
        }}
      >
        <Box component="span" aria-hidden="true" sx={caretLeftSx} />
      </IconButton>
      {paginationRange?.map((pageNumber, index) => {
        if (pageNumber === DOTS) {
          return (
            <Box
              key={`dots-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                ...
              </Typography>
            </Box>
          );
        }

        const isCurrentPage = pageNumber === currentPage;

        return (
          <Button
            key={pageNumber}
            variant={isCurrentPage ? "contained" : "outlined"}
            onClick={() => onPageChange(pageNumber as number)}
            aria-current={isCurrentPage ? "page" : undefined}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              p: 0,
              fontSize: "0.875rem",
              fontWeight: isCurrentPage ? 600 : 400,
              ...(isCurrentPage && {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }),
              ...(!isCurrentPage && {
                borderColor: "custom.ruleHairline",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "primary.main",
                },
              }),
            }}
          >
            {pageNumber}
          </Button>
        );
      })}
      <IconButton
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="Go to next page"
        sx={{
          border: 1,
          borderColor: "custom.ruleHairline",
          width: 36,
          height: 36,
          "&:hover": {
            backgroundColor: "action.hover",
            borderColor: "primary.main",
          },
          "&.Mui-disabled": {
            borderColor: "custom.ruleHairline",
            color: "action.disabled",
          },
        }}
      >
        <Box component="span" aria-hidden="true" sx={caretRightEndSx} />
      </IconButton>
    </Box>
  );
}
